import React from 'react'
import Button from './button'
import Stack from './stack'
import { motion, AnimatePresence } from 'framer-motion'

interface IPopConfirmProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  title?: string
  confirmText?: string
  denyText?: string
  variant?: 'primary' | 'success' | 'error'
  onConfirm?: () => void
  onDeny?: () => void
}

const PopConfirm: React.FC<IPopConfirmProps & any> = ({
  children,
  title = 'Are you sure?',
  confirmText = 'Yes',
  denyText = 'No',
  variant = 'primary',
  onConfirm,
  onDeny,
  ...rest
}) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false)

  const node = React.useRef<HTMLDivElement>(null)

  const handleKeyDown = React.useCallback((e: any) => {
    if (e.keyCode === 27) {
      setIsOpen(false)
    }
  }, [])

  const handleClick = React.useCallback((e: any) => {
    if (!node.current || node.current.contains(e.target)) {
      return
    }

    setIsOpen(false)
  }, [])

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClick)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('click', handleClick)
    }
  }, [handleKeyDown, handleClick])

  return (
    <div ref={node} className='popconfirm-wrapper'>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            transition={{ ease: 'easeOut' }}
            initial={{ opacity: 0, scale: 0.8, x: '-50%' }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className='popconfirm-content'
            {...rest}
          >
            <Stack block direction='column'>
              <h4>{title}</h4>
              <Stack size='small' style={{ justifyContent: 'flex-end' }} block>
                <Button
                  onClick={() => {
                    setIsOpen(false)
                    if (onDeny) {
                      onDeny()
                    }
                  }}
                  size='small'
                >
                  {denyText}
                </Button>
                <Button
                  onClick={() => {
                    setIsOpen(false)
                    if (onConfirm) {
                      onConfirm()
                    }
                  }}
                  size='small'
                  variant={variant}
                >
                  {confirmText}
                </Button>
              </Stack>
            </Stack>
          </motion.div>
        )}
      </AnimatePresence>

      <div onClick={() => setIsOpen(true)}>{children}</div>
    </div>
  )
}

export default PopConfirm
